import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";

// Shared presentational controls for the Historical DLR pipeline kickoff pages
// (SingleProjectKickoff and BulkKickoff). Keeping these here means both pages
// render an identical phase checkbox, date range, and overwrite/resume controls.

// Checkbox + "Phase N: Name" / description block used at the top of each phase card.
export function PhaseHeader({ phase, checked, onToggle }) {
    return (
        <div style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: "8px" }}>
            <FormControlLabel
                control={<Checkbox checked={checked} onChange={onToggle} />}
                label={
                    <div>
                        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                            Phase {phase.number}: {phase.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {phase.description}
                        </Typography>
                    </div>
                }
            />
        </div>
    );
}

// Phase 3 Start/End date inputs. Returned as a fragment so each page can place
// them in its own flex row alongside any page-specific controls. `dateState` is
// the result of computePhase3DateRange.
export function StartEndDateFields({ startDate, setStartDate, endDate, setEndDate, dateState }) {
    const { startDateIsFuture, endDateIsFuture, dateRangeError } = dateState;
    return (
        <>
            <TextField
                label="Start Date"
                type="date"
                size="small"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText={
                    startDateIsFuture
                        ? "Today or future — will use default (5 years ago)"
                        : dateRangeError
                        ? dateRangeError
                        : "Optional — defaults to 5 years ago"
                }
                error={!!dateRangeError && !startDateIsFuture}
                FormHelperTextProps={startDateIsFuture ? { style: { color: "#e6a817" } } : undefined}
                style={{ minWidth: "180px" }}
            />
            <TextField
                label="End Date"
                type="date"
                size="small"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText={
                    endDateIsFuture
                        ? "Today or future — will use default (today)"
                        : dateRangeError
                        ? dateRangeError
                        : "Optional — defaults to today"
                }
                error={!!dateRangeError && !endDateIsFuture}
                FormHelperTextProps={endDateIsFuture ? { style: { color: "#e6a817" } } : undefined}
                style={{ minWidth: "180px" }}
            />
        </>
    );
}

// Resume toggle, used by both Phase 1 (resume = don't overwrite existing outputs)
// and Phase 3 (resume = continue an incomplete run). `onChange` receives the new
// boolean value; `caption` lets each phase explain what resuming means there.
export function ResumeCheckbox({ checked, onChange, caption = "Continue from a previous incomplete run" }) {
    return (
        <FormControlLabel
            control={<Checkbox checked={checked} onChange={(e) => onChange(e.target.checked)} />}
            label={
                <div>
                    <Typography variant="body2">Resume</Typography>
                    <Typography variant="caption" color="textSecondary">
                        {caption}
                    </Typography>
                </div>
            }
        />
    );
}
